import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiUsers, FiShield, FiMail, FiClock, FiBarChart3 } = FiIcons;

const HomePage = () => {
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: FiCalendar,
      title: 'Flexible Kalender',
      description: 'Erstellen Sie Kalender mit variablen Zeitslots und teilen Sie diese über einfache Links.'
    },
    {
      icon: FiUsers,
      title: 'Einfache Buchungen',
      description: 'Ihre Kunden können ohne Registrierung Termine buchen - schnell und unkompliziert.'
    },
    {
      icon: FiShield,
      title: 'Datenschutz',
      description: 'Identitäten bleiben geschützt. Nur Sie als Kalenderbesitzer sehen die Buchungsdetails.'
    },
    {
      icon: FiMail,
      title: 'Automatische E-Mails',
      description: 'Bestätigungen und Erinnerungen werden automatisch an alle Beteiligten gesendet.'
    },
    {
      icon: FiBarChart3,
      title: 'Terminumfragen',
      description: 'Erstellen Sie Umfragen für optimale Terminabstimmungen mit mehreren Teilnehmern.'
    },
    {
      icon: FiClock,
      title: 'Echtzeit-Updates',
      description: 'Verfügbarkeiten werden in Echtzeit aktualisiert und Doppelbuchungen vermieden.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e8f1f3 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-bold mb-6 font-rubik"
              style={{ color: '#2b6777' }}
            >
              Terminbuchung
              <span className="block" style={{ color: '#2b6777' }}>leicht gemacht</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl mb-8 max-w-3xl mx-auto font-rubik"
              style={{ color: '#2b6777' }}
            >
              Die moderne Lösung für Terminbuchungen und Umfragen. Einfach zu verwenden, 
              datenschutzkonform und mit allen Features, die Sie benötigen.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              {isAuthenticated ? (
                <Link to="/dashboard">
                  <Button size="lg" className="w-full sm:w-auto">
                    Zum Dashboard
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/register">
                    <Button size="lg" className="w-full sm:w-auto">
                      Kostenlos starten
                    </Button>
                  </Link>
                  <Link to="/login">
                    <Button variant="outline" size="lg" className="w-full sm:w-auto">
                      Anmelden
                    </Button>
                  </Link>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-rubik" style={{ color: '#2b6777' }}>
              Alles was Sie brauchen
            </h2>
            <p className="text-lg max-w-2xl mx-auto font-rubik" style={{ color: '#2b6777' }}>
              Unsere Plattform bietet alle Features für professionelle Terminverwaltung
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card hover className="p-6 h-full">
                  <div className="flex items-center mb-4">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: '#e8f1f3' }}>
                      <SafeIcon 
                        icon={feature.icon} 
                        className="w-6 h-6"
                        style={{ color: '#2b6777' }}
                      />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-2 font-rubik" style={{ color: '#2b6777' }}>
                    {feature.title}
                  </h3>
                  <p className="font-rubik" style={{ color: '#2b6777' }}>
                    {feature.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#2b6777' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold text-white mb-4 font-rubik"
          >
            Bereit loszulegen?
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-white mb-8 font-rubik opacity-90"
          >
            Erstellen Sie Ihren ersten Kalender in weniger als 2 Minuten
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {!isAuthenticated && (
              <Link to="/register">
                <Button 
                  variant="secondary" 
                  size="lg"
                  style={{ backgroundColor: '#ffffff', color: '#2b6777' }}
                >
                  Jetzt registrieren
                </Button>
              </Link>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;